'use client';

import { useEffect, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { monitoringService } from '../services/monitoring';

interface MonitoringProviderProps {
  children: React.ReactNode;
  connectionString?: string;
}

export function MonitoringProvider({ children, connectionString }: MonitoringProviderProps) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [isConsentGiven, setIsConsentGiven] = useState(monitoringService.isConsentGiven());

  useEffect(() => {
    // Initialize monitoring service (which starts listening to CMP)
    const appInsightsConnectionString = connectionString || import.meta.env.VITE_APP_INSIGHTS_CONNECTION_STRING;
    monitoringService.initialize(appInsightsConnectionString);

    // Subscribe to consent changes
    const unsubscribe = monitoringService.subscribe((consent) => {
      setIsConsentGiven(consent);
    });

    return () => {
      unsubscribe();
    };
  }, [connectionString]);

  useEffect(() => {
    // Track page views on route change if consent is given
    if (isConsentGiven) {
      const pagePath = location.pathname + location.search;
      monitoringService.trackPageView(pagePath, {
        navigationType,
        referrer: document.referrer,
      });
    }
  }, [location, navigationType, isConsentGiven]);

  // Handle errors
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (monitoringService.isConsentGiven()) {
        monitoringService.trackException(
          new Error(event.message),
          {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          }
        );
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (monitoringService.isConsentGiven()) {
        monitoringService.trackException(
          new Error(event.reason?.message || 'Unhandled Promise Rejection'),
          {
            reason: event.reason,
          }
        );
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const reactPlugin = monitoringService.getReactPlugin();

  if (reactPlugin && isConsentGiven) {
    return (
      <AppInsightsContext.Provider value={reactPlugin}>
        {children}
      </AppInsightsContext.Provider>
    );
  }

  return <>{children}</>;
}